import React, { Component } from 'react';
import moment from 'moment';
import { withRouter } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { connect } from 'react-redux'

// components
import { DirTable, DirectoryFooter, InvestorDetailRow, InvestorDetailModal } from '/src/pages/directories/components';

// helpers
import * as startupHelper from '/src/pages/directories/helpers/startupDirHelpers';
import { amountFormatter, formatValuationRange } from "/src/lib/helpers";
import { renderProxyAmount } from '/src/pages/directories/helpers/fundingRowHelpers';
import { createColumnSortHandlers, resolveColumnSortKey, resolveViewOrder } from '/src/pages/directories/helpers/columnSortHelpers';
import { createRequestGuard } from '/src/pages/directories/helpers/requestGuard';

// constants
import { RECORD_COUNT_DEFAULT } from '/src/lib/constants';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import { startupFundingFilterConfig } from '/src/pages/directories/constants/filterConfigStartup';
import { testDataFundingRounds } from '/src/components/LockedContent';

// apis
import fundingApi from '/src/apis/fundingApi';

export const FUNDING_TABLE_NAME_SORT_KEY = 'investedin_name';
export const FUNDING_TABLE_HEADERS = [
    { fieldName: 'Date Funded', keyName: 'date_raised', sortKey: 'date_raised' },
    { fieldName: 'Funding Stage', keyName: 'funding_stage_data', sortKey: 'funding_stage_id' },
    { fieldName: 'Investment Vehicle', keyName: 'investment_vehicle_data', sortKey: 'investment_vehicle_id' },
    { fieldName: 'Amount Raised', keyName: 'amount', sortKey: 'amount' },
    { fieldName: "Valuation", keyName: "valuation", sortKey: 'valuation_min' },
    { fieldName: 'Round Size', keyName: 'round_total_amount_raised_data', sortKey: 'round_total_amount' },
    { fieldName: 'Country', keyName: 'location_data', sortKey: 'location_name' },
    { fieldName: 'Primary Industry', keyName: 'primary_industry_data', sortKey: 'primary_industry_name' },
    { fieldName: 'Investors', keyName: 'investors_data', sortKey: 'investor_count' },
    { fieldName: "Verification Status", keyName: "verification_status", sortKey: 'verification_status' }
];
const DEFAULT_FUNDING_ORDER = { field: 'date_raised', order: 'desc' };

export class StartupDirectoryFunding extends Component {

    constructor() {
        super();
        this.entityType = 'FUNDING VIEW';
        this.state = {
            count: 0,
            limit: RECORD_COUNT_DEFAULT.default,
            page: 1,
            data: false,
            loadingDirPage: false,
            selectedFundingStageName: '',
            defaultFilters: {},
            // these are filters that will be applied by default but hidden from the filter UI
            hiddenDefaultFilters: {

            },
            //table headers
            tableHeaders: FUNDING_TABLE_HEADERS,
            removeSkeletonTable: false
        };
        this.isComponentMounted = false;
        // guards against a slower, older filterValues response overwriting a newer one
        // (e.g. clicking sort/a filter repeatedly before the first request returns)
        this.listGuard = createRequestGuard();
    }

    componentDidMount() {
        this.isComponentMounted = true;
    }

    componentDidUpdate(prevProps) {
        const { tempFilter } = this.props;

        if (prevProps.fundingData.refresh !== this.props.fundingData.refresh) {
            // A sort-only change never affects which records match the filters, so the locked-user
            // count fallback below doesn't need to be refetched just because the table was re-sorted.
            const { order: prevOrder, ...prevFilterRest } = prevProps.fundingData.filter || {};
            const { order: nextOrder, ...nextFilterRest } = this.props.fundingData.filter || {};
            const sortOnlyChanged = isEqual(prevFilterRest, nextFilterRest) && !isEqual(prevOrder, nextOrder);
            this.filterValues(tempFilter, sortOnlyChanged);
        }
    }

    componentWillUnmount() {
        this.isComponentMounted = false;
        this.listGuard.cancel();
    }

    updateState = (state, callback) => {
        if (this.isComponentMounted) {
            this.setState(state, callback);
        }
    }

    // Valid fields = the clickable column headers UNION this tab's own "Sort by" dropdown presets (fundingData.sortList) - some presets
    // (e.g. "Total Funding") have no column of their own, so validating against headers alone would wrongly reject them.
    getValidSortFields = () => [
        FUNDING_TABLE_NAME_SORT_KEY,
        ...FUNDING_TABLE_HEADERS.map(h => resolveColumnSortKey(h, this.props.sortKeyOverrides)),
        ...(this.props.fundingData.sortList || []).map(item => item.field)
    ];

    sortHandlers = createColumnSortHandlers(
        () => resolveViewOrder(this.props.fundingData.order, this.props.tempFilter?.order, this.getValidSortFields(), DEFAULT_FUNDING_ORDER),
        (order) => this.props.changeOrder(order),
        { isEnabled: () => this.props.enableColumnSort, getSortList: () => this.props.fundingData.sortList }
    );

    handleColumnSort = this.sortHandlers.handleColumnSort;
    renderSortHeader = this.sortHandlers.renderSortHeader;

    filterValues = (filter = "", sortOnlyChanged = false) => {

        // only start a request when fetching, else it cancels an in-flight one with nothing to replace it
        const hasDirectoryAccess = this.props.user.roles.includes(MAGNITT_ACCESS_ROLES.directory_access);
        const { id: requestId, signal } = (hasDirectoryAccess || !sortOnlyChanged) ? this.listGuard.start() : {};

        const { isPageChanged } = filter
        delete filter.isPageChanged;

        let { limit, page } = this.state;
        let {
            totalAmountMinSuffix, totalAmountMaxSuffix, amountraisedMinSuffix, amountraisedMaxSuffix, fundingData,
            filterConfig = startupFundingFilterConfig,
            prepFilters = startupHelper.prepNewFiltersFunding,
            fetchRoundsData = fundingApi.getFundingRoundsData,
            fetchBreakdownsCount = fundingApi.getFundingBreakdownsCount,
        } = this.props

        const selectedTempFilter = { ...filter, ...this.state.defaultFilters };

        this.props.setState({
            fundingViewFilters: {
                defaultFilters: this.state.defaultFilters,
                selectedFilters: selectedTempFilter
            }
        });

        const tempFilter = { ...selectedTempFilter, ...this.state.hiddenDefaultFilters };

        // Prefer this tab's own remembered order; Startup.js doesn't track it yet, so
        // fall back to tempFilter.order only if it's actually a field this tab can sort by.
        const activeOrder = resolveViewOrder(fundingData.order, this.props.tempFilter?.order, this.getValidSortFields(), DEFAULT_FUNDING_ORDER);
        const sort = `${activeOrder.field}^:${activeOrder.order}`;
        const { finalFilters } = prepFilters(tempFilter, cloneDeep(filterConfig), totalAmountMinSuffix, totalAmountMaxSuffix, amountraisedMinSuffix, amountraisedMaxSuffix);
        const newFilters = finalFilters;

        // If page hasn't changed, set it to 1
        // This will help when page was set to > 1 and filters were applied
        if (!isPageChanged) {
            page = 1;
            this.updateState({ page })
        }

        if (hasDirectoryAccess) {

            //add default fields to for funding view
            let extraFields = [];

            this.state.tableHeaders.map((field) => {
                extraFields.push(field.keyName);
            })

            this.updateState({ loadingDirPage: true });
            fetchRoundsData(extraFields, newFilters, limit, page, sort, signal)
                .then(response => {
                    if (this.listGuard.isStale(requestId)) return;

                    if (!isEmpty(response)) {
                        let fundingRounds = response.records;
                        this.updateState({
                            data: fundingRounds,
                            count: response.count,
                            loadingDirPage: false
                        });
                        this.props.setState({ fundingTabCount: response.count })
                    } else {
                        this.updateState({
                            data: [],
                            count: 0,
                            loadingDirPage: false
                        });
                        this.props.setState({ fundingTabCount: 0 })

                    }
                }).catch(err => {
                    if (this.listGuard.isStale(requestId)) return;
                    console.error('StartupDirectoryFunding failed to load funding rounds:', err?.message || err);
                    this.updateState({ data: [], count: 0, loadingDirPage: false });
                });

        } else if (!sortOnlyChanged) {
            // locked users only ever see this count, never the sorted rows themselves, so a
            // sort-only change (no filter change) has nothing new to fetch here.

            const tempFilter = {
                finalFilters: newFilters
            }
            fetchBreakdownsCount([], tempFilter, signal)
                .then(response => {
                    if (this.listGuard.isStale(requestId)) return;

                    if (!isEmpty(response)) {
                        this.updateState({
                            count: response.count,
                        });
                        this.props.setState({ fundingTabCount: response.count })

                    } else {
                        this.props.setState({ fundingTabCount: 0 })
                    }
                }).catch(err => {
                    if (this.listGuard.isStale(requestId)) return;
                    console.error('StartupDirectoryFunding failed to load funding breakdowns count:', err?.message || err);
                    this.props.setState({ fundingTabCount: 0 });
                }
                )

        }

    }

    //format entity data for directory slider
    handleEntityData = () => {

        let entityData = null
        if (this.state.data && this.state.data.length > 0) {
            entityData = this.state.data.map((obj) => {
                let investedInData = null

                if (obj.investedin_data) {
                    investedInData = obj.investedin_data
                    investedInData.logo_data = { path: investedInData.logo || null }
                    investedInData.primary_industry_data = obj.primary_industry_data && obj.primary_industry_data.length > 0 ?
                        obj.primary_industry_data[0] : null
                    investedInData.hq_data = obj.investedin_hq_data || null
                }

                return investedInData
            })
        }
        return entityData
    }

    render() {

        const { selectedInvestorModal } = this.state;

        let content = null;
        let tableContent = {};

        tableContent.rows = [];
        tableContent.pinnedRow = [];

        const { user, isFundingView } = this.props;
        let userHasDirectoryAccess = false;
        if (user && user.roles && user.roles.length > 0) {
            userHasDirectoryAccess = (user.roles.includes(MAGNITT_ACCESS_ROLES.directory_access) || user.roles.includes(MAGNITT_ACCESS_ROLES.limited_directory_access))
        }

        const showTestData = (!user.is_loggedin || !userHasDirectoryAccess)

        let directoryData = null;

        if (showTestData) {
            directoryData = testDataFundingRounds;
        } else if (this.state.data !== false && this.state.data.length) {
            directoryData = this.state.data;
        }
        if (directoryData) {
            content = directoryData.map((funding, i) => {

                if (funding.investedin_data != null) {

                    let investedInName = "";
                    let investedInCode = "";
                    let investedinLogo = "";
                    let investedinType = "";
                    let investedinId = funding.investedin_id;

                    if (!isEmpty(funding.investedin_data)) {
                        investedInName = funding.investedin_data.name;
                        investedInCode = funding.investedin_data.code;
                        investedinLogo = funding.investedin_data.logo;
                        investedinType = funding.investedin_data.type.toLowerCase();
                    }

                    let image;
                    if (showTestData) {
                        image = <div className='startupLogo'></div>
                    } else {
                        if (investedinLogo) {
                            image = investedinLogo;
                            image = <div className='startupLogo' style={{ backgroundImage: `url('${image}')` }}></div>
                        } else {
                            image = <div className='startupLogo'><h2>{investedInName.substr(0, 1).toUpperCase()}</h2></div>
                        }
                    }
                    const url = `/${investedinType}s/${investedInCode}-${investedinId}`;
                    let nameRow = (
                        <a className="d-flex align-items-center" href={url} target="_blank" rel="noopener noreferrer">
                            {image}
                            <div className="flex-column" style={{ marginLeft: 10, marginTop: -4 }}>
                                <div className="startup-name" data-action="expand-slider" data-entityid={investedinId}>
                                    {investedInName}
                                </div>
                            </div>
                        </a>
                    );

                    // send to table
                    tableContent.header = [this.renderSortHeader('Name', FUNDING_TABLE_NAME_SORT_KEY)];
                    let fieldData = [{ name: nameRow }];

                    // check default fields
                    this.state.tableHeaders.map((field) => {

                        let itemField = funding[field.keyName];
                        let tempData = "-";

                        tableContent.header.push(this.renderSortHeader(field.fieldName, resolveColumnSortKey(field, this.props.sortKeyOverrides)));

                        if (field.keyName === 'amount' && !itemField) {
                            itemField = 0;
                        }

                        if (itemField || itemField == 0) {
                            switch (field.keyName) {
                            case "date_raised":
                                if (itemField) {
                                    tempData = moment(itemField).format("MMM YYYY");
                                }
                                break;

                            case "location_data":
                                tempData = itemField.name;
                                break;
                            case "funding_stage_data":
                                // tempData = itemField.is_proxy
                                //     ? <span className="amount--estimated">{itemField.name}</span>
                                //     : itemField.name;
                                tempData = itemField?.name || "Undisclosed";
                                break;

                            case "amount":
                                if (itemField > 0) {
                                    tempData = parseInt(itemField, 10);
                                    tempData = amountFormatter(tempData);
                                } else {
                                    tempData = renderProxyAmount(funding.undisclosed_amount) || "Undisclosed";
                                }
                                break;
                            case "round_total_amount_raised_data":
                                if (Number(itemField?.amount) > 0) {
                                    tempData = parseInt(itemField?.amount, 10);
                                    tempData = <span className={itemField?.has_proxy ? 'amount--estimated' : ''} title={itemField?.has_proxy ? 'Estimated amount' : undefined}>{amountFormatter(tempData)}</span>;
                                } else {
                                    tempData = "Undisclosed";
                                }
                                break;

                            case "valuation":
                                if (!isEmpty(itemField) && (itemField && itemField.min && itemField.max)) {
                                    tempData = (
                                        <span className={itemField.is_estimate ? 'amount--estimated' : ''}>
                                            {formatValuationRange(itemField.min, itemField.max)}
                                        </span>
                                    )
                                } else {
                                    tempData = "-"
                                }

                                break;

                            case "investors_data":
                                if (!isEmpty(itemField)) {
                                    tempData = (
                                        <InvestorDetailRow
                                            investedInName={investedInName}
                                            investorData={itemField}
                                            setState={(s, c) => { this.updateState(s, c) }}
                                            userHasDirectoryAccess={userHasDirectoryAccess}
                                            selectedFundingStageName={funding.funding_stage_data?.name}
                                            isFundingView={true}
                                            isNestedModal={true} // to handle modal-backdrop behavior for nested modals i.e. modals that are not a direct child of body
                                        />
                                    )
                                }
                                break;

                            case "primary_industry_data":
                                if (itemField && itemField.length > 0) {
                                    let arrTemp = [];
                                    itemField.forEach(ind => {
                                        if (ind && ind.name) {
                                            arrTemp.push(ind.name);
                                        }
                                    })
                                    tempData = arrTemp.length > 0 ? arrTemp.join(", ") : "-";
                                }
                                break;

                            case "investment_vehicle_data":
                                tempData = itemField.name ? itemField.name : "N/A"
                                break;
                            case "verification_status":
                                tempData = <div className="funding-verified-status" data-verif={itemField}></div>;
                                break;

                            default:
                                tempData = "-";
                            }
                        }

                        fieldData.push({ [field.keyName]: tempData });

                    })

                    tableContent.rows.push(fieldData);
                }
            })

            const fullyLocked = (!user.is_loggedin || !userHasDirectoryAccess); // only sample data displayed

            content = (
                <DirTable
                    content={tableContent}
                    user={this.props.user}
                    entityType={this.entityType}
                    fullyLocked={fullyLocked}
                    setState={(s, c) => { this.updateState(s, c) }}
                    isFundingView={true}
                />
            );

        }

        return (
            <div>

                {content}

                <DirectoryFooter
                    // data
                    count={this.state.count}
                    data={this.state.data}
                    loadingDirPage={this.state.loadingDirPage}
                    allowNavigateWhileLoading
                    showTestData={showTestData}
                    user={this.props.user}
                    page={this.state.page}
                    limit={this.state.limit}
                    tempFilter={this.props.tempFilter}
                    // func
                    clearFilter={this.props.clearFilter}
                    filterValues={this.filterValues}
                    setState={(s, c) => { this.updateState(s, c) }}
                    removeSkeletonTable={this.state.removeSkeletonTable}
                    tabIsVisible={isFundingView}
                />

                {/* note: checking isFundingView to avoid conflicts between the 2 InvestorDetailModals */}
                {/* (funding view + table view) */}
                {isFundingView &&
                    <InvestorDetailModal
                        investedInName={this.state.investedInName}
                        investedInId={this.state.investedInId}
                        investorData={selectedInvestorModal}
                        userHasDirectoryAccess={userHasDirectoryAccess}
                        selectedFundingStageName={this.state.selectedFundingStageName}
                        isFundingView={isFundingView}
                    />
                }

            </div>
        )

    }
}

const mapStateToProps = (state) => ({
    user: state.userReducer
})

export default connect(mapStateToProps)(withRouter(StartupDirectoryFunding))
